import { useQuery } from '@tanstack/react-query';
import axios from 'axios';

import { useAuth } from '../../hooks';
import type { PaginatedItems } from './common.ts';
import type { Direction } from './types/getTransactions';
import { ACCOUNTS_PAGE_SIZE } from './useAccounts.ts';

export interface AccountHistory {
	date: string;
	payer: Payer;
	beneficiary: Beneficiary;
	documentNumber: string;
	details: string;
	endToEnd: string;
	amount: Amount;
	direction: Direction;
}

export interface Amount {
	amount: number;
	currency: string;
	display: string;
}

export interface Payer {
	id: any;
	name: string;
	accountId: string;
	bic: string;
	iban: string;
}

export interface Beneficiary {
	id: string;
	name: string;
	accountId: string;
	bic: string;
	iban: string;
}

export interface Pagination {
	limit: number;
	offset: number;
	count: number;
}

export const ACCOUNT_STATEMENT_PAGE_SIZE = 50;

const useHistory = (
	page: number,
	limit = ACCOUNTS_PAGE_SIZE,
	customerId?: string,
	accountUuid?: string,
	dateFrom?: string,
	dateTo?: string,
	beneficiary?: string,
	description?: string,
	sortBy?: string,
) => {
	const { session } = useAuth();

	const [sortField, sortOrder] = sortBy?.split('-') || ['processing_started_at', 'desc'];
	const sortParam = sortOrder === 'asc' ? sortField : `-${sortField}`;

	const params = {
		page,
		per_page: limit,
		...(dateFrom ? { 'filter[processing_started_at][from]': dateFrom } : {}),
		...(dateTo ? { 'filter[processing_started_at][to]': dateTo } : {}),
		...(description ? { 'filter[payment_details]': description } : {}),
		...(beneficiary ? { 'filter[beneficiary_account_name]': beneficiary } : {}),
		...(sortBy ? { sort: sortParam } : {}),
	};

	const fetchAccounts = async (): Promise<PaginatedItems<AccountHistory[]>> => {
		const response = await axios.get(
			`${import.meta.env.VITE_API_HOST}/api/v1/profiles/${customerId}/accounts/${accountUuid}/history`,
			{
				params,
				headers: { Authorization: session?.tokenized },
			},
		);

		return response.data;
	};

	return useQuery({
		queryKey: [
			'history',
			customerId,
			accountUuid,
			page,
			limit,
			dateFrom,
			dateTo,
			beneficiary,
			description,
			sortField,
			sortOrder,
		],
		queryFn: fetchAccounts,
		enabled: !!customerId && !!accountUuid && !!session?.tokenized,
	});
};

export { useHistory };
